import * as Popover from "@radix-ui/react-popover";
import type { ReactNode } from "react";
import { type TranslateFn, formatDate } from "#/lib/i18n.js";

export type Source = {
  url: string;
  label: string;
  verifiedAt: string; // ISO timestamp
};

type Props = {
  sources: Source[];
  locale: string;
  t: TranslateFn;
  /**
   * When true, the wrapped numeric content stays inline and the source
   * glyph is rendered as a sibling. When false (default) we render the
   * children inside the popover trigger so the whole numeric becomes
   * the activator (rare).
   */
  siblingGlyph?: boolean;
  ariaLabel?: string;
  children: ReactNode;
};

export function SourceCitation({
  sources,
  locale,
  t,
  siblingGlyph = true,
  ariaLabel,
  children,
}: Props) {
  if (!sources || sources.length === 0) {
    return <>{children}</>;
  }

  const latest = sources.reduce((a, b) =>
    new Date(a.verifiedAt) > new Date(b.verifiedAt) ? a : b,
  );
  const latestDate = formatDate(latest.verifiedAt, locale, {
    dateStyle: "medium",
  });

  const computedAria =
    ariaLabel ??
    (sources.length > 1
      ? t("citations.ariaMultiple", {
          count: sources.length,
          date: latestDate,
        })
      : t("citations.ariaSingle", {
          label: latest.label,
          date: latestDate,
        }));

  const glyph = (
    <Popover.Trigger aria-label={computedAria} type="button" className="source-glyph">
      ?
    </Popover.Trigger>
  );

  return (
    <Popover.Root>
      {siblingGlyph ? (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            minInlineSize: "max-content",
          }}
        >
          {children}
          {glyph}
        </span>
      ) : (
        <>
          {children}
          {glyph}
        </>
      )}
      <Popover.Portal>
        <Popover.Content
          className="source-popover"
          sideOffset={6}
          align="end"
          collisionPadding={12}
        >
          <div role="group" aria-label={t("citations.label")}>
            {sources.length > 1 ? (
              <p
                style={{
                  margin: 0,
                  marginBlockEnd: "0.5rem",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  color: "var(--color-fg-muted)",
                }}
              >
                {t("citations.multiple")} ({sources.length})
              </p>
            ) : null}
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "grid",
                gap: "0.5rem",
              }}
            >
              {sources.map((s) => (
                <li key={s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "var(--color-accent-info)",
                      textDecoration: "underline",
                      fontWeight: 500,
                    }}
                  >
                    {s.label}
                  </a>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--color-fg-muted)",
                      marginBlockStart: "0.125rem",
                    }}
                  >
                    {t("citations.verifiedAt", {
                      date: formatDate(s.verifiedAt, locale, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      }),
                    })}
                  </div>
                </li>
              ))}
            </ul>
            <div
              style={{
                marginBlockStart: "0.75rem",
                paddingBlockStart: "0.625rem",
                borderBlockStart: "1px solid var(--color-border-subtle)",
                fontSize: "0.75rem",
              }}
            >
              <a href="/docs/data-sources" style={{ color: "var(--color-fg-muted)" }}>
                {t("citations.aboutSources")} →
              </a>
            </div>
          </div>
          <Popover.Arrow style={{ fill: "var(--color-bg-surface-elevated)" }} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
